import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { fetchSubjects } from '@/services/AdminFetchServices';
import { useGradeBands, useCurricula } from '@/hooks/useValueCatalog';

interface Step1Data {
  subject: string;
  gradeBand: string;
  curriculum: string;
}

interface Step1Props {
  initialSubject?: string;
  initialGradeBand?: string;
  initialCurriculum?: string;
  onNext: (data: Step1Data) => void;
}

export function Step1SubjectGrade({ initialSubject = '', initialGradeBand = '', initialCurriculum = '', onNext }: Step1Props) {
  const [subject, setSubject] = useState(initialSubject);
  const [gradeBand, setGradeBand] = useState(initialGradeBand);
  const [curriculum, setCurriculum] = useState(initialCurriculum);
  const [subjects, setSubjects] = useState<string[]>([]);
  const gradeBands = useGradeBands();
  const curricula = useCurricula();

  useEffect(() => {
    // Same catalog source as the teacher offering "Targeting" tab (fetchSubjects)
    fetchSubjects()
      .then((data: Array<{ name: string }> | null) => {
        if (Array.isArray(data)) setSubjects(data.map((s) => s.name));
      })
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-1">What are you studying?</h2>
        <p className="text-sm text-muted-foreground">Help us find the right teacher for you.</p>
      </div>

      {/* Subject — same catalog + dropdown as the offering "Targeting" tab */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Subject <span className="text-red-500">*</span></label>
        <SearchableSelect
          options={subjects}
          value={subject}
          onChange={setSubject}
          placeholder="Select subject…"
          searchPlaceholder="Search subjects…"
        />
      </div>

      {/* Grade Band */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Grade level <span className="text-red-500">*</span></label>
        {gradeBands.isError ? (
          <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {gradeBands.entries.map(g => (
              <button
                key={g.code}
                type="button"
                disabled={gradeBands.isLoading}
                onClick={() => setGradeBand(prev => prev === g.code ? '' : g.code)}
                className={[
                  'px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-150 cursor-pointer',
                  gradeBand === g.code
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:border-primary/50 hover:bg-muted/50',
                ].join(' ')}
              >
                {g.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Curriculum */}
      {curricula.isError ? (
        <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
      ) : curricula.entries.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Curriculum <span className="text-muted-foreground font-normal">(optional)</span></label>
          <div className="flex flex-wrap gap-2">
            {curricula.entries.map(c => (
              <button
                key={c.code}
                type="button"
                disabled={curricula.isLoading}
                onClick={() => setCurriculum(prev => prev === c.code ? '' : c.code)}
                className={[
                  'px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-150 cursor-pointer',
                  curriculum === c.code
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:border-primary/50 hover:bg-muted/50',
                ].join(' ')}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <Button
        onClick={() => onNext({ subject, gradeBand, curriculum })}
        className="w-full cursor-pointer"
        size="lg"
        disabled={!subject.trim() || !gradeBand}
      >
        Continue
      </Button>
    </div>
  );
}
